import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../utils/supabase';

interface SiteContent {
  id: string;
  section: string;
  key: string;
  value: string;
  type: string;
}

export const useContent = (section?: string) => {
  const [content, setContent] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchContent = useCallback(async () => {
    if (!supabase) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      let query = supabase.from('site_content').select('*');
      
      if (section) {
        query = query.eq('section', section);
      }

      const { data, error } = await query;

      if (error) throw error;

      // Convert array to key-value object
      const contentMap: Record<string, string> = {};
      data?.forEach((item: SiteContent) => {
        const key = section ? item.key : `${item.section}.${item.key}`;
        contentMap[key] = item.value;
      });

      setContent(contentMap);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  }, [section]);

  useEffect(() => {
    fetchContent();
  }, [fetchContent]);

  const getContent = (key: string, defaultValue: string = '') => {
    return content[key] || defaultValue;
  };

  return { content, getContent, loading, error, refetch: fetchContent };
};

// Hook for specific section content
export const useSectionContent = (section: string) => {
  return useContent(section);
};